import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from '@/lib/format'

export interface CardProps extends HTMLAttributes<HTMLElement> {
  /** Remove the inner padding (for tables and lists that run edge to edge). */
  flush?: boolean
  tone?: 'default' | 'danger'
}

/** A surface on the canvas. Use `as="section"` semantics: give it a CardHeader. */
export function Card({ flush, tone = 'default', className, children, ...rest }: CardProps) {
  return (
    <section
      className={cn(
        'min-w-0 rounded-lg border bg-panel',
        tone === 'danger' ? 'border-bad/40' : 'border-line',
        !flush && 'p-4 sm:p-5',
        className,
      )}
      {...rest}
    >
      {children}
    </section>
  )
}

export interface CardHeaderProps {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  /** Heading level for the title (default h2: cards sit under the page h1). */
  as?: 'h2' | 'h3'
  className?: string
}

/**
 * Small-caps card title, as in the mockup ("PAID ADD-ONS"). Not a condensed
 * display heading: those are for page and tenant titles.
 */
export function CardHeader({ title, description, actions, as: Tag = 'h2', className }: CardHeaderProps) {
  return (
    <header className={cn('mb-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-2', className)}>
      <div className="min-w-0">
        <Tag className="font-sans text-2xs font-bold uppercase tracking-eyebrow text-muted">{title}</Tag>
        {description ? <p className="mt-1 text-xs text-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  )
}

/** Definition list: muted terms, tabular values. */
export function KeyValueList({ items, className }: { items: Array<{ label: ReactNode; value: ReactNode }>; className?: string }) {
  return (
    <dl className={cn('grid grid-cols-[minmax(6rem,auto)_1fr] gap-x-4 gap-y-2 text-[13px]', className)}>
      {items.map((item, i) => (
        <div key={i} className="contents">
          <dt className="text-muted">{item.label}</dt>
          <dd className="min-w-0 break-words tabular">{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}
